import { useEffect } from 'react';
import { Minus, Plus } from '../../../../modules/design-system';

export interface QuantityStepperProps {
  value: number;
  min?: number;
  max?: number;
  onChange: (next: number) => void;
  /** md — Figma mobile 35:15118, lg — Figma desktop 17:5533. */
  size?: 'md' | 'lg';
  /** Locks both buttons, e.g. while the pack is restocking. */
  disabled?: boolean;
}

const SIZES = {
  // 116px pod as tall as the Buy button (the dock's button-height knob,
  // 52px), ± buttons filling it 8px in (partner Figma 2026-10-14).
  md: {
    pod: 'h-[var(--co-BottomActionShell-buttonHeight,52px)] w-[116px] rounded-[74px]',
    btn: 'size-[calc(var(--co-BottomActionShell-buttonHeight,52px)_-_16px)]',
    icon: 16,
    number: 'text-[18px] leading-[24px]',
  },
  lg: {
    pod: 'h-[64px] w-[156px] rounded-[38px]',
    btn: 'h-[47px] w-[47px]',
    icon: 21,
    number: 'text-[21px] leading-[26px]',
  },
} as const;

/**
 * [−]  N  [+] on sleek's dark dock (partner mockup): a flat dark pod, two
 * circular ± buttons 8px in from its edge and a white count. Same
 * `--co-QuantityStepper-*` knobs as modern; only the fallbacks are dark.
 * The number uses tabular-nums so its width doesn't jump (1 → 2 → 10).
 */
export function QuantityStepper({
  value,
  min = 1,
  max = 99,
  onChange,
  size = 'md',
  disabled = false,
}: QuantityStepperProps) {
  const s = SIZES[size];
  const canDec = !disabled && value > min;
  const canInc = !disabled && value < max;

  // Keep the value inside [min, max]. Mirrors web QuantityPicker.
  //  - Value below min (e.g. 0 from an earlier clamp) → bump to min.
  //  - Value above max (e.g. stock refresh lowered the cap) → clamp down.
  //  - While max is still loading (< min), leave value alone — don't drag
  //    it to 0 and lock the stepper out.
  useEffect(() => {
    if (value < min) {
      onChange(min);
      return;
    }
    if (max >= min && value > max) {
      onChange(max);
    }
  }, [value, min, max, onChange]);

  const btnClass =
    `${s.btn} shrink-0 rounded-full cursor-pointer ` +
    'border border-[var(--co-QuantityStepper-buttonBorder,transparent)] ' +
    'flex items-center justify-center transition-transform active:scale-95 ' +
    'disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100';

  return (
    <div
      data-co-component="QuantityStepper"
      className={s.pod + ' relative shrink-0'}
      style={{ background: 'var(--co-QuantityStepper-containerFill, #1A1B1F)' }}
    >
      {/* Inner button surface — flat by default, kept so an operator can
          still set a gradient via --co-QuantityStepper-backgroundFrom/To. */}
      <div
        aria-hidden
        className="absolute inset-[4px] rounded-[inherit] border border-[var(--co-QuantityStepper-innerBorder,transparent)]"
        style={{
          backgroundImage:
            'linear-gradient(180deg, ' +
            'var(--co-QuantityStepper-backgroundFrom, #1A1B1F) 0%, ' +
            'var(--co-QuantityStepper-backgroundTo, #1A1B1F) 100%)',
        }}
      />

      {/* Buttons + number */}
      <div className="absolute inset-[8px] flex items-center justify-between">
        <button
          type="button"
          onClick={() => canDec && onChange(value - 1)}
          disabled={!canDec}
          aria-label="Decrease quantity"
          className={btnClass}
          style={{
            backgroundColor: 'var(--co-QuantityStepper-buttonFill, #2F3037)',
            color: 'var(--co-QuantityStepper-buttonIcon, #FFFFFF)',
          }}
        >
          <Minus size={s.icon} />
        </button>

        <span
          className={
            s.number +
            ' font-semibold tracking-[-0.44px] ' +
            "font-['Inter','Chivo_Mono',ui-monospace,monospace] tabular-nums"
          }
          style={{
            color: 'var(--co-QuantityStepper-valueText, #FFFFFF)',
          }}
        >
          {value}
        </span>

        <button
          type="button"
          onClick={() => canInc && onChange(value + 1)}
          disabled={!canInc}
          aria-label="Increase quantity"
          className={btnClass}
          style={{
            backgroundColor: 'var(--co-QuantityStepper-buttonFill, #2F3037)',
            color: 'var(--co-QuantityStepper-buttonIcon, #FFFFFF)',
          }}
        >
          <Plus size={s.icon} />
        </button>
      </div>
    </div>
  );
}
